import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {
  CampaignItem,
  EnabledStatus,
  ICreateOrUpdateItem,
  IDiscountCodeDetails,
  IDiscountCodeListItem,
  ItemsArrayType,
  IUpdateDiscountCodePayload,
  ListResponse,
  LogsItem,
} from '@/models';
import {ICreateDiscountCodePayload, IDiscountCodeLogsItem,} from '../models/discount';
import {changeEnabledStatus, http} from '@/utils';
import {handleApiCallInStore, updateItemById} from './utils';

import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';

export interface IDiscountStore {
  discountCodesList: IDiscountCodeListItem[];
  discountCodeInfo: IDiscountCodeDetails;
  campaigns: CampaignItem[];
  nbHits: number;
  discountCodeLogs: LogsItem[];
  getAllDiscountCodes: (params) => void;
  getDiscountCodeById: (
    id: string,
    onSuccess: (data: IDiscountCodeDetails) => void
  ) => void;
  createOrUpdateDiscountCode: (
    data: ICreateOrUpdateItem<
      IUpdateDiscountCodePayload | ICreateDiscountCodePayload
    >
  ) => void;
  getDiscountCodeLogs: (discountId: string) => void;
  getCampaigns: () => void;
  changeDiscountCodeStatus: (payload: EnabledStatus) => void;
}

export const useDiscountStore = create(
  persist<IDiscountStore>(
    set => ({
      campaigns: [],
      discountCodesList: [],
      discountCodeLogs: [],
      nbHits: 0,
      discountCodeInfo: undefined,
      getAllDiscountCodes: async params => {
        handleApiCallInStore(LoadingKeys.GetAllDiscountCodes, async () => {
          const {data} = await http.get<ListResponse<IDiscountCodeListItem>>(
            API_ENDPOINTS.discountCodes.all,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({discountCodesList: items ?? [], nbHits}));
        });
      },
      changeDiscountCodeStatus: (payload: EnabledStatus) => {
        handleApiCallInStore(
          LoadingKeys.ChangeDiscountCodesStatus,
          async () => {
            const {id, isEnabled} = payload;
            await http.put(`${API_ENDPOINTS.discountCodes.all}/${id}/status`, {
              id,
              isEnabled,
            });

            set(({discountCodesList}) => ({
              discountCodesList: updateItemById(
                discountCodesList,
                'isEnabled',
                isEnabled,
                id
              ),
            }));

            changeEnabledStatus(isEnabled, t('discountCode'));
          }
        );
      },
      getDiscountCodeById: async (id, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetDiscountDetails, async () => {
          const {data} = await http.get<IDiscountCodeDetails>(
            `${API_ENDPOINTS.discountCodes.all}/${id}`
          );
          set(() => ({discountCodeInfo: data}));
          onSuccess(data);
        });
      },
      createOrUpdateDiscountCode: ({id, onSuccess, payload}) => {
        handleApiCallInStore(
          LoadingKeys.CreateOrUpdateDiscountCode,
          async () => {
            const apiLink = `${API_ENDPOINTS.discountCodes.all}${
              id ? `/${id}` : ''
            }`;
            if (id) {
              await http.put(apiLink, payload);
            } else {
              await http.post(apiLink, payload);
            }

            await onSuccess();
          }
        );
      },
      getDiscountCodeLogs: discountId => {
        handleApiCallInStore(LoadingKeys.GetDiscountLogs, async () => {
          const {data} = await http.get<
            ItemsArrayType<IDiscountCodeLogsItem>
          >(
            `${API_ENDPOINTS.logs}${API_ENDPOINTS.discountCodes.all}${API_ENDPOINTS.learners.logs}?id=${discountId}`
          );
          set(() => ({discountCodeLogs: data?.items}));
        });
      },
      getCampaigns: () => {
        handleApiCallInStore(
          LoadingKeys.GetAllDiscountCodesCampings,
          async () => {
            const {data} = await http.get<ItemsArrayType<CampaignItem>>(
              API_ENDPOINTS.campaigns
            );
            set(() => ({campaigns: data?.items}));
          }
        );
      },
    }),
    {
      name: 'learners-storage',

    }
  )
);
